// 12 LLM-native agents powering the Aigentrix autonomous e-invoicing pipeline.
// Data is MOCKED for the frontend clone.

export const agents = [
  {
    id: 1,
    name: "Ingestion & Intent Orchestrator",
    tagline: "Invoice ingestion + intent brain",
    role: "Single entry point for all inbound artefacts and events.",
    icon: "Workflow",
    accent: "from-blue-500 to-indigo-500",
    category: "Ingest",
    status: "Active",
    events: 1284,
    does: [
      "Accepts inputs from all channels: upload, API, SFTP, email, Peppol, EDI, mobile.",
      "Auto-detects artefact type: invoice, credit note, debit note, statement, config.",
      "Classifies business intent (supplier B2B invoice, statement, Peppol XML).",
      "Routes to the correct pipeline automatically.",
    ],
    llm: [
      "Semantic classification of documents and payloads.",
      "Natural-language parsing of email subjects/bodies and API metadata.",
    ],
    input: "Raw artefact + channel metadata",
    output: "Normalised IngestionRecord + ArtefactClassified event",
    outcome: "Zero manual triage; every artefact lands in the right autonomous flow.",
  },
  {
    id: 2,
    name: "Multimodal Capture & Layout Understanding",
    tagline: "Vision + structure engine",
    role: "Vision + structure engine for all documents.",
    icon: "ScanText",
    accent: "from-violet-500 to-fuchsia-500",
    category: "Extract",
    status: "Active",
    events: 1190,
    does: [
      "OCR + vision-LLM interpret complex tables, stamps, logos, handwritten notes.",
      "Produces structured invoice/statement JSON (parties, header, line items, VAT).",
      "Returns confidence scores per field.",
    ],
    llm: [
      "Layout reasoning: distinguish invoice number vs PO number, tax vs total.",
      "Interpret ambiguous labels across languages and formats.",
    ],
    input: "IngestionRecord + raw file",
    output: "DocumentExtracted event + structured payload",
    outcome: "High-fidelity, semantically correct extraction across any format.",
  },
  {
    id: 3,
    name: "Semantic Enrichment & Master Data Reasoning",
    tagline: "ERP-ready, tax-ready entities",
    role: "Turn extracted fields into ERP-ready, tax-ready entities.",
    icon: "Boxes",
    accent: "from-teal-500 to-emerald-500",
    category: "Enrich",
    status: "Active",
    events: 1102,
    does: [
      "Maps suppliers, buyers, items, GL codes using fuzzy + LLM-assisted matching.",
      "Infers missing fields (TRN, Peppol ID, VAT code) from context.",
      "Maintains SCD2 and parent-child relationships.",
    ],
    llm: [
      "Entity resolution: “Are these two names the same supplier?”",
      "Suggest enrichment when master data is incomplete or conflicting.",
    ],
    input: "Extracted JSON + master data + mapping rules",
    output: "InvoiceEnriched event + ERP/tax-ready structure",
    outcome: "Clean, enriched, consistent data with no manual master-data lookup.",
  },
  {
    id: 4,
    name: "Data Quality, Cleansing & Guardrail Reasoning",
    tagline: "Compliance bouncer + auto-fixer",
    role: "Compliance bouncer + auto-fixer.",
    icon: "ShieldCheck",
    accent: "from-amber-500 to-orange-500",
    category: "Validate",
    status: "Active",
    events: 1056,
    does: [
      "Runs deterministic DQ rules: mandatory fields, totals, formats, TRN validity.",
      "Cleanses data (dates, addresses, currency formats, VAT codes).",
      "Decides auto-correct, auto-quarantine, or auto-route to discrepancy workflows.",
    ],
    llm: [
      "Explain DQ failures in plain language.",
      "Judge “typo vs genuine discrepancy” under guardrails.",
    ],
    input: "Enriched invoice + DQ rules",
    output: "InvoiceDQPassed / InvoiceDQFailed + explanation + actions",
    outcome: "Data is clean, explainable, and policy-compliant before any XML step.",
  },
  {
    id: 5,
    name: "VAT & Scenario Reasoning",
    tagline: "Tax brain: rules + reasoning",
    role: "Tax brain combining hard rules + LLM reasoning.",
    icon: "Percent",
    accent: "from-rose-500 to-pink-500",
    category: "Tax",
    status: "Active",
    events: 980,
    does: [
      "Applies codified VAT rules (UAE, KSA, EU): standard, zero, exempt, RCM, OOS.",
      "Detects scenarios: B2B, B2G, export, marketplace, backdated, PO/non-PO.",
      "Produces VAT classification, scenario tags, and justification text.",
    ],
    llm: [
      "Interpret edge cases from free-text (“services performed abroad”, “reverse charge”).",
      "Compare reasoning vs rule engine; flag mismatches for rule tuning.",
    ],
    input: "DQ-passed invoice + tax rules + country config",
    output: "InvoiceClassified event + VAT/scenario + explanation",
    outcome: "Robust, explainable VAT decisions across all scenarios.",
  },
  {
    id: 6,
    name: "Schema, XML & Contract Generation",
    tagline: "Format architect",
    role: "Format architect for all external contracts (tax + network).",
    icon: "FileCode2",
    accent: "from-sky-500 to-cyan-500",
    category: "Generate",
    status: "Active",
    events: 921,
    does: [
      "Generates XML for PINT-AE, UBL 2.1, XRechnung, FatturaPA, ZATCA, etc.",
      "Handles standard invoices, credit notes, debit notes, cancellations.",
      "Validates against schemas and pattern store.",
    ],
    llm: [
      "Generate mapping templates from new authority specs.",
      "Propose updated mappings when schemas change.",
    ],
    input: "Classified invoice + mapping patterns",
    output: "InvoiceXMLGenerated event + XMLDocument artefact",
    outcome: "Rapid adaptation to new regimes with minimal engineering.",
  },
  {
    id: 7,
    name: "Cryptography, Risk & Fraud Insight",
    tagline: "Security + fraud intelligence",
    role: "Security + fraud intelligence layer.",
    icon: "Fingerprint",
    accent: "from-fuchsia-500 to-purple-600",
    category: "Secure",
    status: "Active",
    events: 874,
    does: [
      "Deterministic: UUID/IRN generation, digital signatures, hash chains, QR codes.",
      "Risk: bank detail verification, duplicate detection, unusual-pattern detection.",
      "Generates risk narratives and recommended actions.",
    ],
    llm: [
      "Spot suspicious patterns: “repeated small invoices just under approval threshold”.",
      "“New bank account for high-value supplier” alerts with explanation.",
    ],
    input: "XML + bank/master data + historical patterns",
    output: "InvoiceSigned event + optional FraudAlertRaised",
    outcome: "Cryptographically secure flows plus pattern-level fraud intelligence.",
  },
  {
    id: 8,
    name: "Network & Channel Orchestration",
    tagline: "Smart traffic controller",
    role: "Smart traffic controller across all delivery channels.",
    icon: "Share2",
    accent: "from-indigo-500 to-blue-600",
    category: "Route",
    status: "Active",
    events: 803,
    does: [
      "Chooses channel: Peppol, direct API, email/PDF, portal upload, local gateway.",
      "Applies 5-corner model logic and buyer preferences.",
      "Manages retries, SLAs, and fallbacks.",
    ],
    llm: [
      "Interpret partner contracts/emails to infer routing preferences.",
      "Generate human-readable routing logs and explanations.",
    ],
    input: "Signed XML + scenario + partner profile",
    output: "InvoiceRouted / RoutingFailed + detailed routing log",
    outcome: "Legally correct, business-aware routing with explainable decisions.",
  },
  {
    id: 9,
    name: "Buyer-side, Matching & AP Copilot",
    tagline: "AP decision copilot",
    role: "AP decision copilot on the buyer side.",
    icon: "GitCompareArrows",
    accent: "from-emerald-500 to-teal-600",
    category: "Buyer",
    status: "Active",
    events: 642,
    does: [
      "Validates incoming XML/e-invoices.",
      "Performs 2-way / 3-way matching (PO / GRN / invoice).",
      "Generates ERP posting payloads and approval decisions.",
    ],
    llm: [
      "Explain mismatches (“Qty mismatch due to partial delivery on GRN #…”).",
      "Suggest resolutions (short-pay, dispute, accept with note).",
    ],
    input: "Incoming XML + buyer master data + PO/GRN + rules",
    output: "BuyerInvoiceValidated / Posted / APException + explanation",
    outcome: "AP teams supervise decisions instead of manual matching and posting.",
  },
  {
    id: 10,
    name: "Tax Authority Dialogue & Compliance",
    tagline: "Conversational FTA/ZATCA interface",
    role: "Conversational interface to FTA/ZATCA and other authorities.",
    icon: "Landmark",
    accent: "from-orange-500 to-rose-500",
    category: "Comply",
    status: "Active",
    events: 588,
    does: [
      "Builds TDD, submits to FTA/ZATCA, parses MLS (deterministic).",
      "Maintains MLS lineage and reporting logs.",
      "Provides compliance posture views.",
    ],
    llm: [
      "Interpret MLS errors and propose fixes.",
      "Summarise “why these invoices were rejected”; run what-if simulations.",
    ],
    input: "XML + tax data + authority config",
    output: "TaxReported, MLSReceived + compliance insights",
    outcome: "Transparent, explainable, proactive tax authority interactions.",
  },
  {
    id: 11,
    name: "Reconciliation, VAT Analytics & Narrative",
    tagline: "Finance & tax storyteller",
    role: "Finance & tax storyteller + reconciliation engine.",
    icon: "BarChart3",
    accent: "from-cyan-500 to-blue-500",
    category: "Analyse",
    status: "Active",
    events: 511,
    does: [
      "Supplier statement extraction and matching.",
      "2-way / 3-way reconciliation, portal vs books.",
      "VAT box calculations and return generation.",
    ],
    llm: [
      "Generate narratives: “Why did Box 1 increase this quarter?”",
      "Explain variances and trends; identify mismatch drivers.",
    ],
    input: "Invoices, statements, ledger, MLS, tax rules",
    output: "StatementMatched, VATReturnGenerated + narrative insights",
    outcome: "Finance gets explanations and insights, not just raw numbers.",
  },
  {
    id: 12,
    name: "Lineage, Governance, Pattern & Test Orchestrator",
    tagline: "Governor, librarian & self-test conductor",
    role: "Governor, librarian, and self-test conductor.",
    icon: "GitBranch",
    accent: "from-slate-500 to-gray-700",
    category: "Govern",
    status: "Active",
    events: 466,
    does: [
      "Maintains full lineage graph (events, artefacts, decisions).",
      "Manages pattern store (mappings, DQ rules, VAT rules, templates).",
      "Orchestrates unit, integration, E2E, UAT suites; coordinates canary + rollback.",
    ],
    llm: [
      "Generate new regression tests from incidents.",
      "Suggest rule improvements from recurring patterns; explain lineage to auditors.",
    ],
    input: "All events, artefacts, rules, test results",
    output: "LineageUpdated, TestSuiteCompleted, governance reports",
    outcome: "The platform learns from itself, hardens over time, and stays governable.",
  },
];

export const agentFeed = [
  { agent: 1, text: "Invoice #INV-10293 auto-classified as Export (B2B)", time: "just now", tone: "info" },
  { agent: 5, text: "VAT treatment resolved: Reverse Charge — services performed abroad", time: "2m ago", tone: "info" },
  { agent: 7, text: "Fraud signal: new bank account on high-value supplier ‘Orbit FZC’", time: "6m ago", tone: "warn" },
  { agent: 6, text: "PINT-AE XML generated & schema-validated for 14 invoices", time: "11m ago", tone: "success" },
  { agent: 10, text: "TDD submitted to FTA — MLS received (accepted)", time: "18m ago", tone: "success" },
  { agent: 4, text: "DQ auto-corrected date format on 3 invoices", time: "25m ago", tone: "info" },
  { agent: 8, text: "Routing fallback: Peppol → direct API for buyer ‘Nova Retail’", time: "33m ago", tone: "warn" },
  { agent: 11, text: "Q2 narrative ready: Box 1 up 12% driven by export growth", time: "1h ago", tone: "info" },
];

export const agentStats = [
  { label: "Active agents", value: "12" },
  { label: "Events today", value: "10.4k" },
  { label: "Auto-processed", value: "100%" },
  { label: "Human steps", value: "0" },
];

// Peppol 5-corner delivery model — fully orchestrated by the agents.
export const fiveCorner = [
  { corner: "C1", title: "Supplier", desc: "Issues the invoice", icon: "Building2", color: "from-blue-500 to-indigo-500" },
  { corner: "C2", title: "Supplier ASP", desc: "InSolutions sending access point", icon: "Send", color: "from-violet-500 to-fuchsia-500" },
  { corner: "C3", title: "Buyer ASP", desc: "Receiving access point", icon: "Inbox", color: "from-fuchsia-500 to-pink-500" },
  { corner: "C4", title: "Buyer", desc: "Receives & posts invoice", icon: "Briefcase", color: "from-teal-500 to-emerald-500" },
  { corner: "C5", title: "Tax Authority (FTA)", desc: "Receives TDD / returns MLS", icon: "Landmark", color: "from-orange-500 to-rose-500" },
];
